/**
 * GAP-2 — Plan-of-Record change-request helpers (Brief G supersedes_id chain).
 *
 * A "change request" is not a separate entity: it is a pending catchball proposal
 * on a cycle that already has a ratified/published cycle_report. Accepting it
 * writes version N+1 with supersedes_id → prior report.
 */

import type { AmendmentLogEntry, CycleReport, PlanOfRecord } from '../stores/prioritization/cycleReportStore';

export interface PorChangeRequestContext {
  isChangeRequest: boolean;
  currentVersion: number | null;
  nextVersion: number | null;
  legitimacyState: CycleReport['legitimacyState'] | null;
  reportId: string | null;
}

export function getPorChangeRequestContext(
  currentReport: CycleReport | null | undefined,
): PorChangeRequestContext {
  if (!currentReport) {
    return {
      isChangeRequest: false,
      currentVersion: null,
      nextVersion: null,
      legitimacyState: null,
      reportId: null,
    };
  }
  const currentVersion = currentReport.version ?? 1;
  return {
    isChangeRequest: true,
    currentVersion,
    nextVersion: currentVersion + 1,
    legitimacyState: currentReport.legitimacyState,
    reportId: currentReport.id,
  };
}

/**
 * Client-side preview of what accept would put in amendment_log
 * (same categories as migration 478: added / removed / rank_change).
 */
export function previewPorAmendmentDiff(
  proposedRankedItemIds: string[],
  currentPor: PlanOfRecord | null | undefined,
  itemTitles: Record<string, string> = {},
): AmendmentLogEntry[] {
  if (!currentPor?.ranked_items?.length) return [];

  const previousById = new Map(
    currentPor.ranked_items.map((item) => [item.item_id, item]),
  );
  const proposedSet = new Set(proposedRankedItemIds);
  const entries: AmendmentLogEntry[] = [];

  proposedRankedItemIds.forEach((itemId, index) => {
    const newRank = index + 1;
    const prev = previousById.get(itemId);
    if (!prev) {
      entries.push({
        type: 'added',
        item_id: itemId,
        item_title: itemTitles[itemId] ?? itemId.slice(0, 8),
        previous_rank: null,
        new_rank: newRank,
        justification: null,
        authorized_by_id: null,
        authorized_at: null,
      });
      return;
    }
    if (prev.rank !== newRank) {
      entries.push({
        type: 'rank_change',
        item_id: itemId,
        item_title: prev.title || itemTitles[itemId] || itemId.slice(0, 8),
        previous_rank: prev.rank,
        new_rank: newRank,
        justification: null,
        authorized_by_id: null,
        authorized_at: null,
      });
    }
  });

  for (const prev of currentPor.ranked_items) {
    if (!proposedSet.has(prev.item_id)) {
      entries.push({
        type: 'removed',
        item_id: prev.item_id,
        item_title: prev.title,
        previous_rank: prev.rank,
        new_rank: null,
        justification: null,
        authorized_by_id: null,
        authorized_at: null,
      });
    }
  }

  return entries;
}

export function amendmentTypeLabel(type: AmendmentLogEntry['type']): string {
  switch (type) {
    case 'added':
      return 'Added';
    case 'removed':
      return 'Removed';
    case 'rank_change':
      return 'Rank change';
    default:
      return type;
  }
}
